import React, { useState, useEffect, useRef } from "react";
import { Box, Typography, TextField, InputAdornment, IconButton } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { getSocket } from "../../../socket/socket.js";
import HoverActions from "../HoverActions.jsx";
import ThreadModal from "../ThreadModal.jsx";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { fetchMessages, sendFileMessage, markMessagesRead } from "../../../slice/chatSlice.jsx";

const getParticipantId = (field) =>
  (typeof field === "object" ? field?._id : field)?.toString();
import FilePreview_ from "../FilePreview.jsx";
import OnlineIndicator from "../OnlineIndicator.jsx";
import InputBar from "../InputBar.jsx";
import TypingIndicator from "../TypingIndicator.jsx";
import ReplyMessage from "../ReplyMessage.jsx";
import { clearReadCount } from "../../../slice/uiSlice.jsx";
import ComprehensiveMessage from "../ComprehensiveMessage.jsx";
import ReadReceipt from "../ReadReceipt.jsx";
import ComprehensiveFileMessage from "../ComprehensiveFileMessage.jsx";
import ForwardModal from "../ForwardModal.jsx";
import AttachmentPreview from "../AttachmentPreview.jsx";
import LinkifiedText from "../LinkifiedText.jsx";
import CloseIcon from "@mui/icons-material/Close";
import MicIcon from "@mui/icons-material/Mic";

const SpeechRecognitionAPI = window.SpeechRecognition || window.webkitSpeechRecognition;

const Smallwindow = ({ selectedUser, onClose }) => {
   const socket = getSocket();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth.user);
  const { messages } = useSelector((state) => state.chat);
  const typingIndicators = useSelector((state) => state.ui.typingIndicators || {});
  const threadUnreadCounts = useSelector((state) => state.thread.threadNotifications.unreadCounts || {});

  const [message, setMessage] = useState("");
  const [file, setFile] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [hoveredMessageId, setHoveredMessageId] = useState(null);
  const [replyToMessage, setReplyToMessage] = useState(null);
  const isTypingRef = useRef(false);
  const [threadModalOpen, setThreadModalOpen] = useState(false);
  const [selectedThreadMessageId, setSelectedThreadMessageId] = useState(null);
  const [isHoveringFile, setIsHoveringFile] = useState(false);
  const [forwardModalOpen, setForwardModalOpen] = useState(false);
  const [messageToForward, setMessageToForward] = useState(null);
  const [isDictating, setIsDictating] = useState(false);
  const dictationRef = useRef(null);
  const dictationBaseRef = useRef("");
  const dictationFinalRef = useRef("");

  const toggleDictation = () => {
    if (isDictating) {
      dictationRef.current?.stop();
      return;
    }
    if (!SpeechRecognitionAPI) return;

    const recognition = new SpeechRecognitionAPI();
    recognition.lang = "en-US";
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;

    dictationBaseRef.current = message ? message + " " : "";
    dictationFinalRef.current = "";

    recognition.onstart = () => setIsDictating(true);
    recognition.onend = () => {
      setIsDictating(false);
      dictationRef.current = null;
    };
    recognition.onerror = () => {
      setIsDictating(false);
      dictationRef.current = null;
    };
    recognition.onresult = (event) => {
      let interim = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        if (event.results[i].isFinal) {
          dictationFinalRef.current += event.results[i][0].transcript + " ";
        } else {
          interim += event.results[i][0].transcript;
        }
      }
      setMessage((dictationBaseRef.current + dictationFinalRef.current + interim).trim());
    };

    dictationRef.current = recognition;
    recognition.start();
  };

  const containerRef = useRef(null);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const textInputRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const hoverTimeoutRef = useRef(null);
  const messageRefs = useRef({});

  const lastReadMessageId = React.useMemo(() => {
    if (!messages || messages.length === 0) return null;
    const currentUserId = user.id?.toString();
    for (let i = messages.length - 1; i >= 0; i--) {
      const msg = messages[i];
      const isSentByMe = getParticipantId(msg.sender) === currentUserId;
      if (isSentByMe && msg.isRead) {
        return msg._id;
      }
    }
    return null;
  }, [messages, user.id]);

  const [currentUser, setCurrentUser] = useState(
    selectedUser || JSON.parse(localStorage.getItem("selectedUser")) || { _id: null, name: "select user" }
  );

  const otherUserTyping = typingIndicators[currentUser?._id];

  useEffect(() => {
    if (selectedUser) {
      localStorage.setItem("selectedUser", JSON.stringify(selectedUser));
      setCurrentUser(selectedUser);
    }
  }, [selectedUser?._id]);

  useEffect(() => {
    if (!currentUser?._id) return;
    dispatch(fetchMessages({ senderId: user.id, receiverId: currentUser._id }));
    dispatch(clearReadCount(currentUser));
    
    // Clear any drafted message, files, or replies when switching users
    setMessage("");
    setFile(null);
    setReplyToMessage(null);

    // Immediately focus the input when switching users
    if (textInputRef.current) {
      textInputRef.current.focus();
    }

    return () => {
      // CLEANUP: Prevent 'Typing...' false alarms leaking to the old user!
      if (isTypingRef.current) {
        isTypingRef.current = false;
        const socket = getSocket();
        if (socket && currentUser?._id) {
          socket.emit("stopTyping", { sender: user.id, receiver: currentUser._id });
        }
      }
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, [currentUser?._id, dispatch, user.id]);

  useEffect(() => {
    const socket = getSocket();
    const handleReceive = () => {
      dispatch(fetchMessages({ senderId: user.id, receiverId: currentUser._id }));
    };
    const handleMessageRead = ({ messageId, readAt }) => {
      if (!messageId) return;
      dispatch(markMessagesRead({ messageIds: [messageId], readAt }));
    };
    socket.on("receiveMessage", handleReceive);
    socket.on("messagesavedtodb", handleReceive);
    socket.on("DeleteMessage", handleReceive);
    socket.on("messageRead", handleMessageRead);
    return () => {
      socket.off("receiveMessage", handleReceive);
      socket.off("messagesavedtodb", handleReceive);
      socket.off("DeleteMessage", handleReceive);
      socket.off("messageRead", handleMessageRead);
    };
  }, [currentUser, user.id, dispatch]);

  useEffect(() => {
    if (messages.length > 0) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  const handleTypingStart = () => {
    const socket = getSocket();
    if (!socket || !currentUser?._id) return;
    
    // Always refresh the timeout so it doesn't drop while actively typing
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => handleTypingStop(), 3000);

    // Only emit to the network if we aren't already typing
    if (isTypingRef.current) return;
    
    isTypingRef.current = true;
    socket.emit("startTyping", { sender: user.id, receiver: currentUser._id, senderName: user.firstName || "Someone" });
  };

  const handleTypingStop = () => {
    const socket = getSocket();
    if (!socket || !currentUser?._id || !isTypingRef.current) return;
    isTypingRef.current = false;
    socket.emit("stopTyping", { sender: user.id, receiver: currentUser._id });
  };

  const jumpToMessage = (messageId) => {
    const element = messageRefs.current[messageId];
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      element.classList.add('jump-highlight');
      setTimeout(() => element.classList.remove('jump-highlight'), 2000);
    }
  };

  const handleForwardMessage = (targetUser) => {
    const socket = getSocket();
    if (!socket || !messageToForward || !targetUser) return;

    socket.emit("sendMessage", {
      sender: user.id,
      receiver: targetUser._id,
      content: messageToForward.content,
      isForwarded: true,
      originalSender: messageToForward.senderName === 'You' ? (user.firstName || user.name || "User") : messageToForward.senderName,
      ...(messageToForward.isfile && {
        isfile: true,
        fileUrl: messageToForward.fileUrl,
        filename: messageToForward.filename || messageToForward.file,
        filetype: messageToForward.filetype,
        attachments: messageToForward.attachments || []
      })
    });

    setForwardModalOpen(false);
    setMessageToForward(null);
    if (targetUser._id === currentUser?._id) {
       dispatch(fetchMessages({ senderId: user.id, receiverId: currentUser._id }));
    }
  };

  const sendMessage = async () => {
    handleTypingStop();
    const socket = getSocket();
    if (!socket || (!message.trim() && !file)) return;

    if (file && file.length === 1) {
      const formData = new FormData();
      formData.append("sender", user.id);
      formData.append("receiver", currentUser._id);
      formData.append("message", message);
      formData.append("file", file[0]);
      if (replyToMessage) {
        formData.append("isReply", "true");
        formData.append("replyTo", replyToMessage._id);
        formData.append("replyToContent", replyToMessage.content);
        formData.append("replyToSender", replyToMessage.senderName);
      }
      await dispatch(sendFileMessage(formData));
      setFile(null);
      setMessage("");
    } else if (file && file.length > 1) {
      const formData = new FormData();
      formData.append("sender", user.id);
      formData.append("receiver", currentUser._id);
      formData.append("message", message);
      file.forEach((f) => {
        formData.append("attachments", f);
      });
      if (replyToMessage) {
        formData.append("isReply", "true");
        formData.append("replyTo", replyToMessage._id);
        formData.append("replyToContent", replyToMessage.content);
        formData.append("replyToSender", replyToMessage.senderName);
      }
      await dispatch(sendFileMessage(formData));
      setFile(null);
      setMessage("");
    } else {
      socket.emit("sendMessage", {
        sender: user.id,
        receiver: currentUser._id,
        content: message,
        ...(replyToMessage && {
          isReply: true,
          replyTo: replyToMessage._id,
          replyToContent: replyToMessage.content,
          replyToSender: replyToMessage.senderName,
          replyToTimestamp: replyToMessage.sentAt,
        }),
      });
    }
    setMessage("");
    setReplyToMessage(null);
    dispatch(fetchMessages({ senderId: user.id, receiverId: currentUser._id }));
  };
  // RESTORED: Mark messages as read logic
  useEffect(() => {
    if (!socket || messages.length === 0) return;

    const currentUserId = user.id?.toString();
    const unreadIncoming = messages.filter((msg) => {
      if (msg.isRead) return false;
      const receiverId = getParticipantId(msg.receiver);
      const senderId = getParticipantId(msg.sender);
      return receiverId === currentUserId && (senderId !== currentUserId || receiverId === senderId);
    });

    if (unreadIncoming.length > 0) {
      const message_ids = unreadIncoming.map((msg) => msg._id);
      socket.emit("readmessage", { message_ids, user });
      dispatch(clearReadCount(currentUser));
    }
  }, [messages, socket, user, currentUser, dispatch]);

  const handleOpenThread = (id) => { setSelectedThreadMessageId(id); setThreadModalOpen(true); };
  const closeThreadModal = () => { setThreadModalOpen(false); setSelectedThreadMessageId(null); };

  if (!currentUser?._id) return (
    <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748B' }}>
      <Typography sx={{ fontWeight: 500 }}>Select a user to start chatting</Typography>
    </Box>
  );

  return (
    <Box 
      sx={{ 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column', 
        background: '#FFFFFF',
        position: 'relative',
        borderRadius: '16px',
        overflow: 'hidden',
        boxShadow: '0 8px 32px rgba(15,23,42,0.08)',
        fontFamily: "'Inter', sans-serif"
      }}
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => { 
        e.preventDefault(); 
        const files = Array.from(e.dataTransfer.files || []); 
        if (files.length > 0) { 
          setMessage(files.map(file => file.name).join(", ")); 
          setFile(files); 
        } 
      }}
    >
      {/* Header */}
      <Box 
        sx={{ 
          height: '56px', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between', 
          px: '14px', 
          background: 'linear-gradient(90deg, #2a78d1 0%, #1d63b3 100%)',
          boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)',
          borderRadius: 0, // Explicitly zero out
          flexShrink: 0,
          zIndex: 10
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ position: 'relative' }}>
             <Box 
               sx={{ 
                 width: 34, 
                 height: 34, 
                 borderRadius: '50%', 
                 background: 'rgba(255,255,255,0.2)', 
                 display: 'flex', 
                 alignItems: 'center', 
                 justifyContent: 'center',
                 fontWeight: 700,
                 color: '#FFFFFF',
                 fontSize: '12px'
               }}
             >
               {(currentUser?.name || "U")[0].toUpperCase()}
             </Box>
             <Box 
               sx={{ 
                 position: 'absolute', 
                 bottom: 1, 
                 right: 1, 
                 width: 8, 
                 height: 8, 
                 borderRadius: '50%', 
                 background: '#4ADE80',
                 border: '1.5px solid #2a78d1'
               }} 
             />
          </Box>
          <Box>
            <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#FFFFFF', lineHeight: 1.2 }}>
              {currentUser?.name ?? "Messenger"}
            </Typography>
            <Typography sx={{ fontSize: '10px', color: 'rgba(255,255,255,0.8)', fontWeight: 600 }}>
              Active Now
            </Typography>
          </Box>
        </Box>

        <TextField
          size="small"
          placeholder="Search..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          autoFocus
          sx={{
            maxWidth: '100px',
            '& .MuiOutlinedInput-root': {
              height: '28px',
              borderRadius: '14px',
              background: 'rgba(255,255,255,0.15)',
              fontSize: '11px',
              px: 1,
              color: '#FFFFFF',
              '& fieldset': { border: 'none' },
            },
            '& input::placeholder': { color: 'rgba(255,255,255,0.6)', opacity: 1 }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: '14px', color: 'rgba(255,255,255,0.6)' }} />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Messages Area */}
      <Box 
        ref={containerRef}
        sx={{ 
          flex: 1, 
          overflowY: 'auto', 
          overflowX: 'hidden',
          p: '14px', 
          display: 'flex', 
          flexDirection: 'column', 
          gap: 1.5,
          background: '#F8FAFC',
          '&::-webkit-scrollbar': { width: '4px' },
          '&::-webkit-scrollbar-thumb': { background: '#CBD5E1', borderRadius: '10px' }
        }}
      >
        {messages
          .filter(m => (m.content || "").toLowerCase().includes(searchQuery.toLowerCase()))
          .map((msg, index) => {
            const isSentByUser = msg.sender === user.id || msg.sender?._id === user.id;
            const senderName = isSentByUser ? "You" : (currentUser.name || "User");
            const parseMsgDate = (m) => {
              const d = new Date(m?.sentAt || m?.createdAt);
              return isNaN(d.getTime()) ? new Date() : d; // just-arrived message without timestamp
            };
            const sentAt = parseMsgDate(msg);

            const prevMsg = messages[index - 1];
            const showDate = !prevMsg || parseMsgDate(prevMsg).toDateString() !== sentAt.toDateString();

            const dateDivider = showDate && (
              <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', my: 1 }}>
                <Typography sx={{ fontSize: '11px', fontWeight: 600, color: '#64748B', background: '#E2E8F0', px: 1.5, py: 0.25, borderRadius: '10px' }}>
                  {sentAt.toLocaleDateString("en-GB")}
                </Typography>
              </Box>
            );

            if (msg.isDeleted) {
              return (
                <Box
                  key={msg._id || index}
                  ref={el => { if (msg._id) messageRefs.current[msg._id] = el; }}
                  sx={{ display: 'flex', flexDirection: 'column', alignItems: isSentByUser ? 'flex-end' : 'flex-start' }}
                >
                  {dateDivider}
                  <Box
                    sx={{
                      maxWidth: '85%',
                      borderRadius: '12px',
                      background: '#F1F5F9',
                      border: '1px solid #E2E8F0',
                      px: '10px',
                      py: '6px'
                    }}
                  >
                    <Typography sx={{ fontSize: '12px', fontStyle: 'italic', color: '#94A3B8' }}>
                      This message was deleted
                    </Typography>
                  </Box>
                </Box>
              );
            }

            return (
              <Box 
                key={msg._id || index}
                ref={el => { if (msg._id) messageRefs.current[msg._id] = el; }}
                sx={{ display: 'flex', flexDirection: 'column', alignItems: isSentByUser ? 'flex-end' : 'flex-start' }}
              >
                {dateDivider}
                {msg.isReply ? (
                  msg.isfile ? (
                    <ComprehensiveFileMessage
                      msg={msg}
                      isSentByUser={isSentByUser}
                      senderName={senderName}
                      hoveredMessageId={hoveredMessageId}
                      setHoveredMessageId={setHoveredMessageId}
                      HoverActions={HoverActions}
                      setReplyToMessage={setReplyToMessage}
                      onJumpToOriginal={jumpToMessage}
                      onForward={(m) => { setMessageToForward(m); setForwardModalOpen(true); }}
                      isLastRead={msg._id === lastReadMessageId}
                      currentUser={currentUser}
                    />
                  ) : (
                    <ComprehensiveMessage
                      msg={msg}
                      isSentByUser={isSentByUser}
                      senderName={senderName}
                      hoveredMessageId={hoveredMessageId}
                      setHoveredMessageId={setHoveredMessageId}
                      HoverActions={HoverActions}
                      setReplyToMessage={setReplyToMessage}
                      handleOpenThread={handleOpenThread}
                      onJumpToOriginal={jumpToMessage}
                      onForward={(m) => { setMessageToForward(m); setForwardModalOpen(true); }}
                      isLastRead={msg._id === lastReadMessageId}
                      currentUser={currentUser}
                    />
                  )
                ) : (
                  <Box 
                    id={`msg-${msg._id}`}
                    onMouseEnter={() => setHoveredMessageId(msg._id)}
                    onMouseLeave={() => setHoveredMessageId(null)}
                    sx={{ 
                      maxWidth: '85%', 
                      borderRadius: '12px',
                      background: isSentByUser ? '#E0F2FE' : '#FFFFFF',
                      color: '#1E293B',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                      position: 'relative',
                      border: '1px solid #E2E8F0',
                      transition: 'all 0.2s ease',
                      '&.jump-highlight': { boxShadow: '0 0 0 3px rgba(125, 211, 252, 0.3)' }
                    }}
                  >
                    {/* Internal Header */}
                    <Box sx={{ 
                      px: '10px', 
                      py: '3px', 
                      background: isSentByUser ? 'rgba(0,0,0,0.04)' : 'rgba(0,0,0,0.02)',
                      borderBottom: '1px solid rgba(0,0,0,0.05)',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: 2
                    }}>
                      <Typography sx={{ fontSize: '10px', fontWeight: 700, opacity: 0.8 }}>
                        {isSentByUser ? 'Me' : senderName}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography sx={{ fontSize: '8px', opacity: 0.6 }}>
                          {sentAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={{ px: '10px', py: '8px' }}>
                      {msg.isForwarded && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5, opacity: 0.6 }}>
                           <Typography sx={{ fontSize: '9px', fontWeight: 700, fontStyle: 'italic' }}>
                             ➡️ Forwarded {msg.originalSender ? `from ${msg.originalSender}` : ""}
                           </Typography>
                        </Box>
                      )}
                      {msg.attachments?.length > 0 ? (
                        <AttachmentPreview attachments={msg.attachments} />
                      ) : msg.isfile ? (
                        <FilePreview_ file={msg.filetype} url={msg.fileUrl} fileName={msg.filename} msg={msg} />
                      ) : (
                        <Typography sx={{ fontSize: '12px', lineHeight: 1.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                          <LinkifiedText text={msg.content} />
                        </Typography>
                      )}
                    </Box>

                    {hoveredMessageId === msg._id && (
                      <Box sx={{ 
                        position: 'absolute', 
                        top: -32, 
                        right: isSentByUser ? 0 : 'auto', 
                        left: isSentByUser ? 'auto' : 0, 
                        zIndex: 20, 
                        scale: '0.75',
                        transformOrigin: isSentByUser ? 'right' : 'left'
                      }}>
                        <HoverActions
                          message={{...msg, senderName}}
                          onReply={setReplyToMessage}
                          onOpenThread={handleOpenThread}
                          onForward={(m) => { setMessageToForward(m); setForwardModalOpen(true); }}
                          currentUser={currentUser}
                        />
                      </Box>
                    )}
                  </Box>
                )}
                 {isSentByUser && (
                  <ReadReceipt
                    isLastRead={msg._id === lastReadMessageId}
                    isRead={msg.isRead}
                    readAt={msg.readAt}
                    currentUser={currentUser}
                  />
                )}
              </Box>
            );
          })}
        <div ref={messagesEndRef} />
      </Box>

      {/* Entry Area */}
      <Box sx={{ p: '10px 14px 12px', background: '#F8FAFC', borderTop: '1px solid #E2E8F0' }}>
        {otherUserTyping?.isTyping && (
           <Box sx={{ mb: 1, px: 1 }}>
             <TypingIndicator who={otherUserTyping.senderName} />
           </Box>
        )}

        <ReplyMessage replyToMessage={replyToMessage} onCancelReply={() => setReplyToMessage(null)} />
        
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1, 
          background: '#FFFFFF', 
          borderRadius: '20px', 
          px: 1.5, 
          py: 0.5,
          mt: replyToMessage ? 1 : 0,
          border: '1px solid #E2E8F0',
          boxShadow: '0 2px 6px rgba(0,0,0,0.02)'
        }}>
          <IconButton size="small" onClick={() => fileInputRef.current?.click()} sx={{ color: '#64748B' }}>
            <Typography sx={{ fontSize: '16px' }}>📎</Typography>
          </IconButton>

          <IconButton
            size="small"
            onClick={toggleDictation}
            disabled={!SpeechRecognitionAPI}
            title={
              !SpeechRecognitionAPI
                ? "Voice dictation isn't supported in this browser. Try Chrome or Edge."
                : isDictating
                ? "Stop dictation"
                : "Dictate message"
            }
            sx={{ color: isDictating ? '#EF4444' : '#64748B', '&.Mui-disabled': { color: '#CBD5E1' } }}
          >
            <MicIcon sx={{ fontSize: '16px' }} />
          </IconButton>

          <TextField
            inputRef={textInputRef}
            fullWidth
            size="small"
            multiline
            maxRows={3}
            value={message}
            onChange={(e) => { 
              setMessage(e.target.value); 
              if (e.target.value.trim()) {
                handleTypingStart();
              } else {
                handleTypingStop();
              }
            }}
            onPaste={(e) => {
              const items = e.clipboardData?.items;
              if (!items) return;
              const files = [];
              for (const item of items) {
                if (item.kind === "file") {
                  const file = item.getAsFile();
                  if (file) files.push(file);
                }
              }
              if (files.length > 0) {
                e.preventDefault();
                setMessage(files.map(file => file.name).join(", "));
                setFile(files);
              }
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                sendMessage();
              }
            }}
            placeholder="Type..."
            sx={{ 
              '& .MuiOutlinedInput-root': { 
                '& fieldset': { border: 'none' },
                fontSize: '12px',
                padding: '4px 0'
              } 
            }}
          />

          <IconButton 
            size="small"
            onClick={sendMessage}
            disabled={!message.trim() && !file}
            sx={{ 
              color: '#FFFFFF',
              background: message.trim() || file ? '#2a78d1' : '#CBD5E1',
              width: '28px',
              height: '28px',
              '&:hover': { background: message.trim() || file ? '#1d63b3' : '#CBD5E1' }
            }}
          >
            <Typography sx={{ fontSize: '14px', fontWeight: 700 }}>↑</Typography>
          </IconButton>
        </Box>
      </Box>

      <input type="file" multiple ref={fileInputRef} onChange={(e) => { 
        const files = Array.from(e.target.files || []); 
        if (files.length > 0) { 
          setMessage(files.map(file => file.name).join(", ")); 
          setFile(files); 
        } 
      }} style={{ display: "none" }} />
      <ThreadModal open={threadModalOpen} onClose={() => setThreadModalOpen(false)} messageId={selectedThreadMessageId} />
      <ForwardModal open={forwardModalOpen} onClose={() => setForwardModalOpen(false)} onForward={handleForwardMessage} message={messageToForward} />
    </Box>
  );
};

export default Smallwindow;
